import { SolarDay, SolarMonth, SolarTerm, SolarTime } from 'tyme4ts';

function pad(value) {
  return String(value).padStart(2, '0');
}

function sameDay(dayA, dayB) {
  return dayA.toString() === dayB.toString();
}

function formatDateKey(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function getSolarDayFromDate(date) {
  return SolarDay.fromYmd(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

function getSolarDayFromKey(key) {
  const [year, month, day] = key.split('-').map(Number);
  return SolarDay.fromYmd(year, month, day);
}

function buildSolarTimeForDay(solarDay, now = new Date()) {
  const todayKey = toDateKey(now);
  const targetKey = formatDateKey(solarDay.getYear(), solarDay.getMonth(), solarDay.getDay());
  const hour = targetKey === todayKey ? now.getHours() : 12;
  const minute = targetKey === todayKey ? now.getMinutes() : 0;
  const second = targetKey === todayKey ? now.getSeconds() : 0;
  return SolarTime.fromYmdHms(solarDay.getYear(), solarDay.getMonth(), solarDay.getDay(), hour, minute, second);
}

function formatSolarTime(solarTime) {
  return `${solarTime.getYear()}年${solarTime.getMonth()}月${solarTime.getDay()}日 ${pad(solarTime.getHour())}:${pad(solarTime.getMinute())}`;
}

function formatSolarDayLabel(solarDay) {
  return `${solarDay.getYear()}年${solarDay.getMonth()}月${solarDay.getDay()}日 周${solarDay.getWeek().getName()}`;
}

function formatSolarDayShort(solarDay) {
  return `${solarDay.getMonth()}月${solarDay.getDay()}日`;
}

function formatTermTime(term) {
  const solarTime = term.getJulianDay().getSolarTime();
  return `${solarTime.getMonth()}月${solarTime.getDay()}日 ${pad(solarTime.getHour())}:${pad(solarTime.getMinute())}`;
}

function formatClock(solarTime) {
  return `${pad(solarTime.getHour())}:${pad(solarTime.getMinute())}`;
}

function getFestivalSummary(solarDay) {
  const legalHoliday = solarDay.getLegalHoliday();
  const solarFestival = solarDay.getFestival();
  const lunarFestival = solarDay.getLunarDay().getFestival();
  const term = solarDay.getTerm();
  const termSolarDay = term.getSolarDay();

  const items = [];

  if (legalHoliday) {
    items.push({
      name: legalHoliday.getName(),
      tone: legalHoliday.isWork() ? 'work' : 'rest'
    });
  }
  if (solarFestival) {
    items.push({
      name: solarFestival.getName(),
      tone: 'festival'
    });
  }
  if (lunarFestival) {
    items.push({
      name: lunarFestival.getName(),
      tone: 'festival'
    });
  }
  if (sameDay(termSolarDay, solarDay)) {
    items.push({
      name: term.getName(),
      tone: 'term'
    });
  }

  return items;
}

function getCalendarLabel(solarDay) {
  const festivals = getFestivalSummary(solarDay);
  if (festivals.length) {
    return festivals[0].name;
  }

  const lunarDay = solarDay.getLunarDay();
  if (lunarDay.getDay() === 1) {
    return lunarDay.getLunarMonth().getName();
  }
  return lunarDay.getName();
}

function getConflictText(sixtyCycleDay) {
  const branch = sixtyCycleDay.getSixtyCycle().getEarthBranch();
  return `冲${branch.getOpposite().getZodiac().getName()} 煞${branch.getOminous().getName()}`;
}

function formatList(items, limit = 8) {
  return items.slice(0, limit).map((item) => item.getName());
}

function formatHours(hourList) {
  return hourList.map((hour) => ({
    name: hour.getName(),
    label: hour.getSixtyCycle().getName(),
    keeper: hour.getTwelveStar().getName(),
    channel: hour.getTwelveStar().getEcliptic().getName(),
    luck: hour.getTwelveStar().getEcliptic().getLuck().getName(),
    recommends: formatList(hour.getRecommends(), 2)
  }));
}

export function toDateKey(date = new Date()) {
  return formatDateKey(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

export function getMonthAnchorKey(date = new Date()) {
  return formatDateKey(date.getFullYear(), date.getMonth() + 1, 1);
}

export function shiftMonthAnchorKey(anchorKey, offset) {
  const solarDay = getSolarDayFromKey(anchorKey);
  const month = solarDay.getSolarMonth().next(offset);
  return formatDateKey(month.getYear(), month.getMonth(), 1);
}

export function buildTodaySnapshot(date = new Date()) {
  const solarTime = SolarTime.fromYmdHms(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  );
  const solarDay = solarTime.getSolarDay();
  const lunarHour = solarTime.getLunarHour();
  const lunarDay = lunarHour.getLunarDay();
  const lunarMonth = lunarDay.getLunarMonth();
  const eightChar = lunarHour.getEightChar();
  const term = solarTime.getTerm();
  const nextTerm = term.next(1);
  const nextTermTime = nextTerm.getJulianDay().getSolarTime();
  const daysToNextTerm = Math.max(
    0,
    Math.ceil(nextTermTime.getJulianDay().getDay() - solarTime.getJulianDay().getDay())
  );

  return {
    solarLabel: formatSolarTime(solarTime),
    solarDayLabel: formatSolarDayLabel(solarDay),
    lunarLabel: `${lunarMonth.isLeap() ? '闰' : ''}${lunarMonth.getName()} ${lunarDay.getName()}`,
    lunarFullLabel: lunarDay.toString(),
    rabByungLabel: solarDay.getRabByungDay().toString(),
    yearPillar: eightChar.getYear().getName(),
    monthPillar: eightChar.getMonth().getName(),
    dayPillar: eightChar.getDay().getName(),
    hourPillar: eightChar.getHour().getName(),
    constellation: solarDay.getConstellation().getName(),
    zodiac: lunarDay.getYearSixtyCycle().getEarthBranch().getZodiac().getName(),
    phase: solarDay.getPhase().getName(),
    phaseDay: solarDay.getPhaseDay().toString(),
    termName: term.getName(),
    termLabel: solarDay.getTermDay().toString(),
    termTime: formatTermTime(term),
    nextTermName: nextTerm.getName(),
    nextTermTime: formatTermTime(nextTerm),
    daysToNextTerm,
    festivals: getFestivalSummary(solarDay),
    recommends: formatList(lunarDay.getRecommends(), 4),
    avoids: formatList(lunarDay.getAvoids(), 4)
  };
}

export function buildDayDetail(dateKey, now = new Date()) {
  const solarDay = getSolarDayFromKey(dateKey);
  const solarTime = buildSolarTimeForDay(solarDay, now);
  const lunarHour = solarTime.getLunarHour();
  const lunarDay = lunarHour.getLunarDay();
  const eightChar = lunarHour.getEightChar();
  const term = solarDay.getTerm();
  const sixtyCycleDay = solarDay.getSixtyCycleDay();
  const dayStem = sixtyCycleDay.getSixtyCycle().getHeavenStem();
  const festivals = getFestivalSummary(solarDay);

  return {
    key: dateKey,
    solarLabel: formatSolarDayLabel(solarDay),
    lunarLabel: lunarDay.toString(),
    rabByungLabel: solarDay.getRabByungDay().toString(),
    solarText: solarDay.toString(),
    lunarText: `${lunarDay.getLunarMonth().isLeap() ? '闰' : ''}${lunarDay.getLunarMonth().getName()} ${lunarDay.getName()}`,
    constellation: solarDay.getConstellation().getName(),
    zodiac: lunarDay.getYearSixtyCycle().getEarthBranch().getZodiac().getName(),
    yearPillar: eightChar.getYear().getName(),
    monthPillar: eightChar.getMonth().getName(),
    dayPillar: eightChar.getDay().getName(),
    hourPillar: eightChar.getHour().getName(),
    pengZu: sixtyCycleDay.getSixtyCycle().getPengZu().getName(),
    recommends: formatList(lunarDay.getRecommends(), 12),
    avoids: formatList(lunarDay.getAvoids(), 12),
    gods: formatList(lunarDay.getGods(), 12),
    hourLuck: formatHours(sixtyCycleDay.getHours()),
    summaryRows: [
      { label: '节气', value: solarDay.getTermDay().toString() },
      { label: '下个节气', value: `${term.next(1).getName()} · ${formatTermTime(term.next(1))}` },
      { label: '月相', value: `${solarDay.getPhase().getName()} · ${solarDay.getPhaseDay().toString()}` },
      { label: '星座生肖', value: `${solarDay.getConstellation().getName()}座 · ${lunarDay.getYearSixtyCycle().getEarthBranch().getZodiac().getName()}年` },
      { label: '节日信息', value: festivals.length ? festivals.map((item) => item.name).join('、') : '无' },
      { label: '法定信息', value: solarDay.getLegalHoliday() ? `${solarDay.getLegalHoliday().getName()}${solarDay.getLegalHoliday().isWork() ? '（调班）' : '（休）'}` : '无' },
      { label: '藏历', value: solarDay.getRabByungDay().toString() },
      { label: '喜神方位', value: dayStem.getJoyDirection().getName() },
      { label: '福神方位', value: dayStem.getMascotDirection().getName() },
      { label: '财神方位', value: dayStem.getWealthDirection().getName() },
      { label: '太岁方位', value: lunarDay.getJupiterDirection().getName() },
      { label: '纳音', value: sixtyCycleDay.getSixtyCycle().getSound().getName() },
      { label: '冲煞', value: getConflictText(sixtyCycleDay) },
      {
        label: '值神',
        value: `${lunarDay.getTwelveStar().getName()} · ${lunarDay.getTwelveStar().getEcliptic().getName()} · ${lunarDay.getTwelveStar().getEcliptic().getLuck().getName()}`
      },
      { label: '建除', value: lunarDay.getDuty().getName() },
      { label: '二十八宿', value: `${lunarDay.getTwentyEightStar().getName()}${lunarDay.getTwentyEightStar().getAnimal().getName()} · ${lunarDay.getTwentyEightStar().getLuck().getName()}` },
      { label: '今日胎神', value: lunarDay.getFetusDay().getName() },
      { label: '人元司令', value: solarDay.getHideHeavenStemDay().getName() },
      {
        label: '小六壬',
        value: `${lunarDay.getMinorRen().getName()} · ${lunarDay.getMinorRen().getLuck().getName()}`
      },
      {
        label: '九星',
        value: `${lunarDay.getNineStar().getName()} · ${lunarDay.getNineStar().getColor()} · ${lunarDay.getNineStar().getElement().getName()}`
      }
    ],
    festivals,
    legalHoliday: solarDay.getLegalHoliday()
      ? {
          name: solarDay.getLegalHoliday().getName(),
          isWork: solarDay.getLegalHoliday().isWork()
        }
      : null,
    termInfo: {
      current: term.getName(),
      currentTime: formatTermTime(term),
      next: term.next(1).getName(),
      nextTime: formatTermTime(term.next(1))
    }
  };
}

export function buildUpcomingEvents(baseDate = new Date(), limit = 3) {
  const baseDay = getSolarDayFromDate(baseDate);
  const result = [];
  const seen = new Set();

  for (let i = 1; i <= 220; i += 1) {
    const day = baseDay.next(i);
    const legal = day.getLegalHoliday();
    const solarFestival = day.getFestival();
    const lunarFestival = day.getLunarDay().getFestival();
    const term = day.getTerm();
    const events = [];

    if (legal && !legal.isWork()) {
      events.push({ name: legal.getName(), tone: 'rest' });
    }
    if (solarFestival) {
      events.push({ name: solarFestival.getName(), tone: 'festival' });
    }
    if (lunarFestival) {
      events.push({ name: lunarFestival.getName(), tone: 'festival' });
    }
    if (sameDay(term.getSolarDay(), day)) {
      events.push({ name: term.getName(), tone: 'term' });
    }

    for (const event of events) {
      const key = event.name;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push({
        ...event,
        dateLabel: `${day.getMonth()}月${day.getDay()}日`,
        daysAway: i,
        displayDays: `${i}天后`
      });
      if (result.length >= limit) {
        return result;
      }
    }
  }

  return result;
}

export function buildMonthCalendar(anchorKey, selectedKey, now = new Date()) {
  const anchor = getSolarDayFromKey(anchorKey);
  const month = SolarMonth.fromYm(anchor.getYear(), anchor.getMonth());
  const selectedDay = getSolarDayFromKey(selectedKey);
  const today = getSolarDayFromDate(now);

  return {
    title: `${month.getYear()}年${month.getMonth()}月`,
    prevAnchorKey: shiftMonthAnchorKey(anchorKey, -1),
    nextAnchorKey: shiftMonthAnchorKey(anchorKey, 1),
    weeks: month.getWeeks(1).map((week) =>
      week.getDays().map((day) => {
        const dateKey = formatDateKey(day.getYear(), day.getMonth(), day.getDay());
        const festivals = getFestivalSummary(day);
        return {
          key: dateKey,
          solarDay: day.getDay(),
          lunarText: getCalendarLabel(day),
          markerTone: festivals[0]?.tone || '',
          isCurrentMonth: day.getMonth() === month.getMonth(),
          isToday: sameDay(day, today),
          isSelected: sameDay(day, selectedDay)
        };
      })
    )
  };
}

export function buildSolarTermsYear(year, now = new Date()) {
  const targetYear = Number(year);
  const start = SolarTerm.fromIndex(targetYear, 0);
  const currentJulianDay = SolarTime.fromYmdHms(
    now.getFullYear(),
    now.getMonth() + 1,
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds()
  ).getJulianDay().getDay();

  const terms = [];
  let currentTermKey = '';

  for (let i = 0; i < 26; i += 1) {
    const term = start.next(i);
    const solarTime = term.getJulianDay().getSolarTime();
    const solarDay = solarTime.getSolarDay();

    if (solarDay.getYear() !== targetYear) {
      continue;
    }

    const nextTerm = term.next(1);
    const termJulianDay = term.getJulianDay().getDay();
    const nextJulianDay = nextTerm.getJulianDay().getDay();
    const phenology = solarDay.getPhenology();
    const key = `${solarDay.getYear()}-${pad(solarDay.getMonth())}-${pad(solarDay.getDay())}-${term.getName()}`;
    const isCurrent = currentJulianDay >= termJulianDay && currentJulianDay < nextJulianDay;

    if (isCurrent) {
      currentTermKey = key;
    }

    terms.push({
      key,
      name: term.getName(),
      kind: term.isJie() ? '节' : '气',
      dateLabel: formatSolarDayShort(solarDay),
      timeLabel: formatClock(solarTime),
      solarLabel: formatSolarDayLabel(solarDay),
      termDayLabel: solarDay.getTermDay().toString(),
      isCurrent,
      isPast: currentJulianDay >= nextJulianDay,
      phenologyLabel: `${phenology.getName()} · ${phenology.getThreePhenology().getName()}`,
      phenologies: [0, 1, 2].map((offset) => {
        const item = phenology.next(offset);
        return {
          name: item.getName(),
          stage: item.getThreePhenology().getName()
        };
      }),
      constellation: solarDay.getConstellation().getName(),
      phaseLabel: `${solarDay.getPhase().getName()} · ${solarDay.getPhaseDay().toString()}`,
      rabByungLabel: solarDay.getRabByungDay().toString()
    });
  }

  return {
    year: targetYear,
    title: `${targetYear}年节气时令`,
    currentTermKey,
    terms
  };
}
